import React, { useEffect } from 'react';
import { BrowserRouter, Route, Switch, Redirect } from 'react-router-dom';
import Navigation from '../Navigation/Navigation';
import SignUp from '../../pages/SignUp/SignUp';
import SignIn from '../../pages/SignIn/SignIn';
import PasswordForget from '../../pages/PasswordForget/PasswordForget';
import AddAudio from '../../pages/AddAudio/AddAudio';
import EditAudio from '../../pages/EditAudio/';
import Account from '../../pages/Account/Account';
import Invite from '../../pages/Invite';
import ClaimLicense from '../../pages/ClaimLicense';
import ReviewSpot from '../../pages/ReviewSpot/ReviewSpot';
import DeliveryPage from '../../pages/Delivery/DeliveryPage';
import RapidUploadPage from '../../pages/RapidUpload/RapidUploadPage';
import Jobs from '../../pages/Jobs/Jobs';
import JobDetail from '../../pages/Jobs/JobDetail';
import SelectOrganization from '../../pages/SelectOrganization';
import GetStarted from '../../pages/GetStarted';
import Dashboard from '../../pages/Dashboard/Dashboard';
import Admin from '../../pages/Admin/Admin.jsx';
import ModalProvider from '../utility/Modal/ModalProvider';
import ModalRoot from '../utility/Modal/ModalRoot';
import GlobalPlayerProvider from '../utility/GlobalPlayer/GlobalPlayerProvider';
import GlobalPlayerRoot from '../utility/GlobalPlayer/GlobalPlayerRoot';
import LoaderOverlayProvider from '../LoaderOverlay/LoaderOverlayContext';
import LoaderOverlayRoot from '../LoaderOverlay/LoaderOverlay';
import * as ROUTES from '../../constants/routes';
import { withAuthentication } from '../Session';
import './app.scss';
import TracksListener from '../../listeners/TrackListener';
import PublicTracksListener from '../../listeners/PublicTracksListener';
import Firebase, { FirebaseContext } from '../Firebase';
import { useCollectionCache } from '../../services/collectionCache.service';
import { useAuthStore } from '../../store/auth.store';
import { useOrganizationStore } from '../../store/organization.store';
import { useChatbotStore } from '../../store/chatbot.store';
import { useVersionCheck } from '../../hooks/useVersionCheck';
import { useNotificationsStore } from '../../store/notifications.store';
import FindAudioWithPlaylistFilter from '../../pages/FindAudio/FindAudioWithPlaylistFilter';
import PublicFindAudio from '../../pages/FindAudio/PublicFindAudio';
import { PlaylistProvider } from '../../components/PlaylistContext';
import { ErrorBoundary, RouteErrorBoundary, ComponentErrorBoundary } from '../ErrorBoundary';
import { PendingLicensesModal } from '../PendingLicensesModal';
import PendingLicenseRequestsModal from '../PendingLicenseRequestsModal';
import PendingReviewsModal from '../PendingReviewsModal';
import ReviewResultsModal from '../ReviewResultsModal';
import DeliveryNotificationsModal from '../DeliveryNotificationsModal/DeliveryNotificationsModal';
import { ExpiredLicensesModal } from '../ExpiredLicensesModal';
import { ExclusiveLicenseNotificationModal } from '../ExclusiveLicenseNotificationModal';
import DeliveryRejectionsModal from '../DeliveryRejectionsModal/DeliveryRejectionsModal';
import AccessRequestsModal from '../AccessRequestsModal/AccessRequestsModal';
import AccessRequestResultsModal from '../AccessRequestsModal/AccessRequestResultsModal';
// [FTP-MIGRATION 2026-06-15] FTP access request/approve flow is dormant
// while FTP is only used to send to the current org's own FTP from the
// Download Manager. See docs/FTP_DOWNLOAD_MANAGER_MIGRATION.md.
// import FtpAccessRequestsModal from '../FTPSettings/FtpAccessRequestsModal';
// import FtpAccessResultsModal from '../FTPSettings/FtpAccessResultsModal';
import ActionBannerHost from '../global/ActionBannerHost';

// Custom Modules — bespoke, per-client tools. Lazy-loaded so the JS
// bundle doesn't grow for orgs that don't have module access. Each
// module directory hosts its own entry. Kept BELOW all imports to
// satisfy the `import/first` ESLint rule (React.lazy is a statement,
// not an import — mixing them scatters imports through the module).
const ModulesIndex = React.lazy(() => import('../../pages/Custom_modules/ModulesIndex'));
const CheckersModule = React.lazy(() => import('../../pages/Custom_modules/Checkers'));
const ShopriteModule = React.lazy(() => import('../../pages/Custom_modules/Shoprite'));
const OkFoodsModule = React.lazy(() => import('../../pages/Custom_modules/OK_Foods'));
const ModulesAdmin = React.lazy(() => import('../../pages/Admin/ModulesAdmin'));
const DeliveryOverridesAdmin = React.lazy(() => import('../../pages/Admin/DeliveryOverridesAdmin'));

const firebase = new Firebase();

const App = () => {
  // Prompt the user to reload when a newer version has been deployed.
  useVersionCheck();

  const loadEssentialCollections = useCollectionCache(state => state.loadEssentialCollections);
  const loadCollectionData = useCollectionCache(state => state.loadCollectionData);
  const clearCache = useCollectionCache(state => state.clearCache);
  const fetchChatbotConfig = useChatbotStore(state => state.fetchConfig);
  const setAuth = useAuthStore(state => state.setAuth);
  const setRole = useAuthStore(state => state.setRole);

  // Organization store actions
  const setOrganizations = useOrganizationStore(state => state.setOrganizations);
  const setCurrentOrganization = useOrganizationStore(state => state.setCurrentOrganization);
  const setSuperAdmin = useOrganizationStore(state => state.setSuperAdmin);
  const setMasterOrg = useOrganizationStore(state => state.setMasterOrg);
  const setCurrentOrgLogo = useOrganizationStore(state => state.setCurrentOrgLogo);
  const setCurrentOrgDeliveryOverrides = useOrganizationStore(state => state.setCurrentOrgDeliveryOverrides);
  const setCurrentOrgDeliveryOverridesEnabled = useOrganizationStore(state => state.setCurrentOrgDeliveryOverridesEnabled);
  const setInAppPrefs = useNotificationsStore(state => state.setInAppPrefs);
  const resetInAppPrefs = useNotificationsStore(state => state.resetInAppPrefs);
  const setInAppEnabledSince = useNotificationsStore(state => state.setInAppEnabledSince);
  const resetInAppEnabledSince = useNotificationsStore(state => state.resetInAppEnabledSince);
  const setOrgLoading = useOrganizationStore(state => state.setLoading);
  const clearOrganization = useOrganizationStore(state => state.clearOrganization);

  // Initialize collection cache and handle auth state changes
  useEffect(() => {
    const unsubscribe = firebase.onAuthUserListener(
      async (authUser: any) => {
        if (authUser) {
          // IMPORTANT: Set org loading FIRST before setting auth
          // This prevents the SignIn page from redirecting before orgs are loaded
          setOrgLoading(true);

          // Set auth in store
          setAuth(authUser);

          // Load essential collections (genres, tags) for chatbot functionality
          // Must be done after auth since Firestore rules require authentication
          loadEssentialCollections(firebase);
          // Load Gemini API key for AI features (chase list parsing, chatbot)
          fetchChatbotConfig(firebase);

          // Set role based on explicit role checks
          const isAdmin = authUser.roles && authUser.roles['ADMIN'] === 'ADMIN';
          const isOperator = authUser.roles && authUser.roles['ADMIN'] === 'OPERATOR';

          // Set role and load collections based on role
          if (isAdmin) {
            setRole('ADMIN');
          } else if (isOperator) {
            setRole('OPERATOR');
          } else {
            setRole('USER');
          }

          // Handle organization membership
          try {
            // Check for super admin status
            const isSuperAdmin = authUser.isSuperAdmin === true;
            setSuperAdmin(isSuperAdmin);

            // Check for organizations pending restoration
            // (orgs where user is in members subcollection but org has restoreRequested flag)
            await firebase.checkAndRestorePendingOrgs(authUser.uid);

            // Get user's organizations from their profile (refresh after potential restore)
            const organizations = await firebase.getUserOrganizations(authUser.uid);
            setOrganizations(organizations);

            // If user has organizations and currentOrgId is persisted, verify it's valid
            // Read directly from store to get the current value (not stale closure)
            let selectedOrgId: string | null = null;
            if (organizations.length > 0) {
              const persistedOrgId = useOrganizationStore.getState().currentOrgId;
              const validOrg = organizations.find((o: any) => o.orgId === persistedOrgId);

              if (validOrg) {
                // Use the persisted org
                setCurrentOrganization(validOrg.orgId, validOrg.orgName, validOrg.role, validOrg.orgType || 'artist', validOrg.orgCategory);
                selectedOrgId = validOrg.orgId;
              } else if (organizations.length === 1) {
                // Only auto-select if user has exactly one org
                const firstOrg = organizations[0];
                setCurrentOrganization(firstOrg.orgId, firstOrg.orgName, firstOrg.role, firstOrg.orgType || 'artist', firstOrg.orgCategory);
                selectedOrgId = firstOrg.orgId;
              } else {
                // Persisted org is invalid for this user and they have multiple orgs
                // Clear the invalid persisted orgId so they go to SelectOrganization
                clearOrganization();
                setOrganizations(organizations); // Re-set orgs after clear
                setSuperAdmin(isSuperAdmin); // Re-set super admin status after clear
              }
            }

            // Check if selected org is a master org and load org-level branding (logoUrl)
            // + delivery overrides (bypassQc / bypassReview).
            if (selectedOrgId) {
              try {
                const { doc: fbDoc, getDoc: fbGetDoc } = await import('firebase/firestore');
                const orgDocRef = fbDoc(firebase.db, 'organizations', selectedOrgId);
                const orgDoc = await fbGetDoc(orgDocRef);
                const orgData = orgDoc.exists() ? orgDoc.data() : null;
                setMasterOrg(orgData?.isMasterOrg === true);
                setCurrentOrgLogo(orgData?.logoUrl || null);
                // Delivery overrides only take effect while the org currently
                // has access. Access revoked → the stored bypass flags are
                // neutralised so gates/banners behave as if unset (the values
                // remain in Firestore and re-apply if access is re-granted).
                const hasDeliveryAccess = orgData?.deliveryOverridesEnabled === true;
                setCurrentOrgDeliveryOverrides({
                  bypassQc: hasDeliveryAccess && orgData?.settings?.bypassQcRequirement === true,
                  bypassReview: hasDeliveryAccess && orgData?.settings?.bypassReviewRequirement === true,
                  bypassQcBeforeReview: hasDeliveryAccess && orgData?.settings?.bypassQcBeforeReview === true,
                });
                setCurrentOrgDeliveryOverridesEnabled(hasDeliveryAccess);
              } catch {
                setMasterOrg(false);
                setCurrentOrgLogo(null);
                setCurrentOrgDeliveryOverrides({ bypassQc: false, bypassReview: false, bypassQcBeforeReview: false });
                setCurrentOrgDeliveryOverridesEnabled(false);
              }

              // Load this user's notification preferences for the selected org
              // so the bell badge + modal queue respect their in-app toggles.
              try {
                const prefs = await firebase.getMemberNotificationPreferences(selectedOrgId, authUser.uid);
                setInAppPrefs(prefs.inApp || {});
                // Hydrate the "enabled since" timestamps so subscribers
                // filter accumulated backlog for types the user muted
                // and later re-enabled.
                setInAppEnabledSince(prefs.inAppEnabledSince || {});
              } catch (err) {
                console.warn('Could not load notification preferences:', err);
                resetInAppPrefs();
                resetInAppEnabledSince();
              }
            } else {
              setMasterOrg(false);
              setCurrentOrgLogo(null);
              setCurrentOrgDeliveryOverrides({ bypassQc: false, bypassReview: false, bypassQcBeforeReview: false });
              setCurrentOrgDeliveryOverridesEnabled(false);
              resetInAppPrefs();
              resetInAppEnabledSince();
            }

            // Load org-scoped collections only if an org is selected
            // Global collections (genres, tags, keys, bpm) are loaded via loadEssentialCollections above
            if (selectedOrgId) {
              loadCollectionData(firebase, selectedOrgId);
            }
          } catch (error) {
            console.error('Error loading organization data:', error);
          } finally {
            setOrgLoading(false);
          }
        } else {
          // Clear auth and role
          setAuth(null);
          setRole('USER');

          // Clear organization state
          clearOrganization();

          // Set loading to false since there's no user to load orgs for
          setOrgLoading(false);

          // Clear collection cache
          clearCache();
        }
      },
      () => {
        // Handle auth error - also need to stop loading
        setAuth(null);
        setRole('USER');
        clearOrganization();
        setOrgLoading(false);
        clearCache();
      }
    );

    return () => {
      unsubscribe();
      clearCache();
    };
  // Note: currentOrgId is intentionally NOT in deps - we read it via getState() inside the callback
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadEssentialCollections, loadCollectionData, clearCache, setAuth, setRole, setOrganizations, setCurrentOrganization, setSuperAdmin, setMasterOrg, setCurrentOrgLogo, setCurrentOrgDeliveryOverrides, setCurrentOrgDeliveryOverridesEnabled, setOrgLoading, clearOrganization, fetchChatbotConfig, setInAppPrefs, resetInAppPrefs, setInAppEnabledSince, resetInAppEnabledSince]);

  return (
    <ErrorBoundary>
      <FirebaseContext.Provider value={firebase}>
        <BrowserRouter>
          <ComponentErrorBoundary name="TracksListener" minimal>
            <TracksListener />
          </ComponentErrorBoundary>
          <ComponentErrorBoundary name="PublicTracksListener" minimal>
            <PublicTracksListener />
          </ComponentErrorBoundary>
          <div>
            <LoaderOverlayProvider>
              <GlobalPlayerProvider>
                <ModalProvider>
                  <ComponentErrorBoundary name="ModalRoot" minimal>
                    <ModalRoot />
                  </ComponentErrorBoundary>

                  <ComponentErrorBoundary name="Navigation">
                    <Navigation />
                  </ComponentErrorBoundary>

                  <PlaylistProvider>
                    <RouteErrorBoundary>
                      <Switch>
                        <Route
                          path={ROUTES.PASSWORD_FORGET}
                          component={PasswordForget}
                        />
                        <Route exact path={ROUTES.HOME} component={Dashboard} />
                        <Route path={ROUTES.FIND_AUDIO} component={FindAudioWithPlaylistFilter} />
                        <Route path={ROUTES.PUBLIC_CATALOGUE} component={PublicFindAudio} />
                        <Route path={ROUTES.MASTER_LIBRARY} component={PublicFindAudio} />
                        <Route path={ROUTES.PUBLIC_LIBRARY} component={PublicFindAudio} />
                        <Route path={ROUTES.ADD_AUDIO} component={AddAudio} />
                        <Route exact path={ROUTES.JOBS} component={Jobs} />
                        <Route path={ROUTES.JOB_DETAIL} component={JobDetail} />
                        <Route
                          path={`${ROUTES.EDIT_AUDIO}/:trackId`}
                          component={EditAudio}
                        />
                        <Route path={ROUTES.ACCOUNT} component={Account} />
                        {/* Custom Modules — admin route BEFORE the
                            broad /admin route so react-router's Switch
                            matches the more-specific path first. */}
                        <Route path={ROUTES.ADMIN_MODULES}>
                          <React.Suspense fallback={null}>
                            <ModulesAdmin />
                          </React.Suspense>
                        </Route>
                        <Route path={ROUTES.ADMIN_DELIVERY_OVERRIDES}>
                          <React.Suspense fallback={null}>
                            <DeliveryOverridesAdmin />
                          </React.Suspense>
                        </Route>
                        <Route path={ROUTES.MODULE_CHECKERS}>
                          <React.Suspense fallback={null}>
                            <CheckersModule />
                          </React.Suspense>
                        </Route>
                        <Route path={ROUTES.MODULE_SHOPRITE}>
                          <React.Suspense fallback={null}>
                            <ShopriteModule />
                          </React.Suspense>
                        </Route>
                        <Route path={ROUTES.MODULE_OK_FOODS}>
                          <React.Suspense fallback={null}>
                            <OkFoodsModule />
                          </React.Suspense>
                        </Route>
                        <Route exact path={ROUTES.MODULES_INDEX}>
                          <React.Suspense fallback={null}>
                            <ModulesIndex />
                          </React.Suspense>
                        </Route>
                        <Route path={ROUTES.ADMIN} component={Admin} />
                        <Route path={ROUTES.SIGN_UP} component={SignUp} />
                        <Route path={ROUTES.INVITE} component={Invite} />
                        <Route path={ROUTES.CLAIM_LICENSE} component={ClaimLicense} />
                        <Route path={ROUTES.REVIEW_SPOT} component={ReviewSpot} />
                        <Route path={ROUTES.DELIVERY} component={DeliveryPage} />
                        <Route path={ROUTES.RAPID_UPLOAD} component={RapidUploadPage} />
                        <Route path={ROUTES.SELECT_ORGANIZATION} component={SelectOrganization} />
                        <Route path={ROUTES.GET_STARTED} component={GetStarted} />
                        <Route exact path={ROUTES.SIGN_IN} component={SignIn} />
                        {/* Catch-all: any unmatched path (e.g. a stale
                            /signin link, a typo'd URL) redirects to the real
                            sign-in page at "/" instead of rendering a blank
                            app shell. Must stay last in the Switch. */}
                        <Route path="*">
                          <Redirect to={ROUTES.SIGN_IN} />
                        </Route>
                      </Switch>
                    </RouteErrorBoundary>
                  </PlaylistProvider>

                  <ComponentErrorBoundary name="GlobalPlayer" minimal>
                    <GlobalPlayerRoot />
                  </ComponentErrorBoundary>

                  {/* Global notification modals — fire on any page */}
                  <PendingLicensesModal firebase={firebase} />
                  <PendingLicenseRequestsModal firebase={firebase} />
                  <PendingReviewsModal firebase={firebase} />
                  <ReviewResultsModal firebase={firebase} />
                  <DeliveryNotificationsModal firebase={firebase} />
                  <ExpiredLicensesModal firebase={firebase} />
                  <ExclusiveLicenseNotificationModal firebase={firebase} />
                  <DeliveryRejectionsModal firebase={firebase} />
                  <AccessRequestsModal firebase={firebase} />
                  <AccessRequestResultsModal firebase={firebase} />
                  {/* [FTP-MIGRATION 2026-06-15] FTP access request modals
                      are dormant. Send-to-FTP is now in the Download Manager
                      and only targets the current org's own FTP.
                  <FtpAccessRequestsModal firebase={firebase} />
                  <FtpAccessResultsModal firebase={firebase} />
                  */}
                  <ActionBannerHost />

                  <ComponentErrorBoundary name="LoaderOverlay" minimal>
                    <LoaderOverlayRoot />
                  </ComponentErrorBoundary>
                </ModalProvider>
              </GlobalPlayerProvider>
            </LoaderOverlayProvider>
          </div>
        </BrowserRouter>
      </FirebaseContext.Provider>
    </ErrorBoundary>
  );
};

export default withAuthentication(App);
